import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "4 years of coding in San Francisco, lessons learned",
  "description": "",
  "published": "2019-04-15T00:00:00.000Z",
  "redirect_from": ["/blog/4-years-of-coding-in-san-francisco-lessons-learned/swizec/9026"],
  "categories": "Uncategorized",
  "hero": "./img/6ou5Jf9.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2019/04/11084974_642240412543178_1151569054_n.jpg",
        "alt": null
      }}></img></p>
    <blockquote>
      <p parentName="blockquote">{`Your code doesn't matter. Your tools are irrelevant. Shipped beats perfect.`}</p>
    </blockquote>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Your code doesn't matter.  Your tools are irrelevant. Shipped beats perfect. `}<a parentName="p" {...{
          "href": "https://t.co/UXW274kR3h"
        }}>{`https://t.co/UXW274kR3h`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1106450826308939778"
      }}>{`March 15, 2019`}</a></blockquote>
    <p>{`That's the biggest lesson I learned working in the heart of Silicon Valley. Outside your team, nobody cares about your code.`}</p>
    <p>{`Your line breaks don't matter. Your single quote, double quote, or backtick debate is a waste of time. Named arguments or not, short functions or long, fat views or models, single file or many, React or Vue, NoSQL or Postgres, ...`}</p>
    <p>{`None of it matters.`}</p>
    <p>{`You know what matters?`}</p>
    <p>{`Did you find customers? Do they enjoy your product? Are they paying? Are you improving the lives of hundreds of people or thousands?`}</p>
    <p><img parentName="p" {...{
        "src": "/51ba73cdf1f44f60d4ed04c1238eaa0a/wp-content-uploads-2015-04-mario11.gif",
        "alt": null
      }}></img></p>
    <p>{`That's what matters.`}</p>
    <p>{`How many lives has your code improved?`}</p>
    <p>{`Until you work on that, nothing else matters.`}</p>
    <h2 {...{
      "id": "your-code-wont-matter-if-youre-dead"
    }}>{`Your code won't matter if you're dead`}</h2>
    <p>{`My former CTO once explained to me when I huffed and puffed into our 1-on-1 that our codebase is terrible and hard to work with and that if we don't do something quick, we soon won't be able to ship features anymore! What would the engineering team come to, if all we ever do is trip over ourselves and our shitty code?`}</p>
    <p><em parentName="p">{`"Look mate, we don't even know if we're gonna be alive in 6 months. What code quality? What impending doom? Just do your best, work around the issues, and let's survive first"`}</em></p>
    <p>{`He was right.`}</p>
    <p>{`You are a talented engineer. You're paid a lot of money. Your job is to keep the duct tape and chewing gum charade going long enough for the business people to even figure out what they want.`}</p>
    <p>{`Companies and products go through stages.`}</p>
    <p>{`At first everything is a prototype. You build it. Try it out. See if it works.`}</p>
    <p>{`Then you rip it out and try something else.`}</p>
    <p>{`How well must you engineer something you're throwing away 3 days after deploy because customers hate it?`}</p>
    <h2 {...{
      "id": "you-die-a-prototype-or-become-that-code-everyone-hates-to-work-with"
    }}>{`You die a prototype or become that code everyone hates to work with`}</h2>
    <p>{`Sometimes your prototype survives, hits its stride, and you're left dealing with your own mess for the next 3 years.`}</p>
    <p>{`That's a good thing!`}</p>
    <p>{`If you don't feel bad looking at your old code, you're not growing. Fact. Your old code always looks like crap.`}</p>
    <p>{`Old code that isn't shitty was over-engineered the first time around. You `}<em parentName="p">{`want`}</em>{` to ship prototype code. Faster iterations, faster learning, company/department/team more likely to survive.`}</p>
    <p>{`You'll fix the code later, if it proves itself.`}</p>
    <p>{`That's a platitude. You won't have time. You'll be working on a million other things :P`}</p>
    <h2 {...{
      "id": "dont-fix-it-until-it-breaks"
    }}>{`Don't fix it until it breaks`}</h2>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`To all my over-engineerers and overthinkerers 👇`}<br parentName="p"></br><br parentName="p"></br>{`We are currently logging over 500,000 events per day to a PostgreSQL database running on a single Heroku instance. The table has over 300,000,000 rows.`}<br parentName="p"></br><br parentName="p"></br>{`Don't worry about scale. Stuff scales. `}<a parentName="p" {...{
          "href": "https://t.co/BljZ0dHkdu"
        }}>{`pic.twitter.com/BljZ0dHkdu`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1049786180483477504"
      }}>{`October 9, 2018`}</a></blockquote>
    <p>{`So when `}<em parentName="p">{`do`}</em>{` you fix old code?`}</p>
    <p>{`When it breaks. When it tells you where it hurts.`}</p>
    <p>{`Would you put a cast on your leg just in case? Maybe you'll get in a car crash and break it. Good thing the cast is already there!`}</p>
    <p>{`Sounds silly right?`}</p>
    <p>{`Yet we do that with code all the time. An article made the rounds recently: `}<a parentName="p" {...{
        "href": "https://blog.bradfieldcs.com/you-are-not-google-84912cf44afb"
      }}>{`You are not Google`}</a>{`.`}</p>
    <p><em parentName="p">{`"Oh noes I better build for scale! I need Hadoop and a million microservices, and I better engineer this real good. What if we get a million users all at once and our Ruby On Rails app buckles?"`}</em></p>
    <p>{`You won't.`}</p>
    <p>{`To quote `}<a parentName="p" {...{
        "href": "https://users.ece.utexas.edu/~adnan/pike.html"
      }}>{`Rob Pike's 5 Rules of Programming`}</a>{`: N is usually small.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Fancy algorithms are slow when n is small, and n is usually small. Fancy algorithms have big constants. Until you know that n is frequently going to be big, don't get fancy. (Even if n does get big, use Rule 2 first.)`}</p>
    </blockquote>
    <p>{`Build it the most obvious simplest way first. When that breaks, make it more complicated, if you have to. Simple systems are easier to fix and they break less often.`}</p>
    <p>{`Optimize for simplicity, not speed or scale.`}</p>
    <h2 {...{
      "id": "relax-constraints"
    }}>{`Relax constraints`}</h2>
    <p>{`You can often make an unsolvable problem solvable by relaxing constraints. When the business people say "the best", they often mean "good enough". When they say "guaranteed" they often mean "unlikely".`}</p>
    <p>{`There is a world of difference between writing software that works well enough in the real world and software that is provably always correct in the mathematical sense.`}</p>
    <p>{`You likely don't need perfection. Ask.`}</p>
    <h2 {...{
      "id": "if-your-code-doesnt-matter-what-does"
    }}>{`If your code doesn't matter, what does?`}</h2>
    <p>{`Can you ship on time?`}</p>
    <p>{`Does it work when you ship?`}</p>
    <p>{`Do your customers love it?`}</p>
    <p>{`Do you `}<em parentName="p">{`have`}</em>{` customers?`}</p>
    <p>{`Can you add features?`}</p>
    <p>{`Can you fix it when it breaks?`}</p>
    <p>{`Not if, when.`}</p>
    <p>{`Can your team work on your code when you can't?`}</p>
    <p>{`Optimize for those and you'll be fine. Write code anyone can understand, write code that's easy to expand, write code that's easy to fix.`}</p>
    <p>{`However you and your team get there, it's all good. React, Vue, Reason, JavaScript, Java, or FORTRAN. Whatever floats your team's boat and gets the software shipped and in user's hands.`}</p>
    <h2 {...{
      "id": "san-francisco-is-expensive-but-worth-it"
    }}>{`San Francisco is expensive but worth it`}</h2>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Omg it's my anniversary month! `}<br parentName="p"></br><br parentName="p"></br>{`4 years since I moved to San Francisco`}<br parentName="p"></br>{`4 years since I launched React + D3`}<br parentName="p"></br>{`4 years living with Girlfriend`}<br parentName="p"></br>{`4 years of being a very settled "digital nomad"`}<br parentName="p"></br>{`4 years with Kiwi 🐣`}<br parentName="p"></br><br parentName="p"></br>{`Any interest in a lessons learned article?`}<a parentName="p" {...{
          "href": "https://t.co/D2aSkS9p4o"
        }}>{`https://t.co/D2aSkS9p4o`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1116735066216919040"
      }}>{`April 12, 2019`}</a></blockquote>
    <p>{`When I first moved to San Francisco a lot of friends back home poked fun at me. It's too expensive they said. It's not worth it. It's covered in shit. Literally. There's too many homeless people. It's too tech bro.`}</p>
    <p>{`Yeah, it's covered in poop. There's heroin needles everywhere. You get used to the homeless people. They mean you no harm.`}</p>
    <p>{`Don't bother them, they won't bother you. It's like you're living past each other. Two people stuck on different sides of the class divide, rarely interacting.`}</p>
    <p>{`It's like a cyberpunk movie.`}</p>
    <p>{`And yeah ... it's expensive. But San Francisco is expensive for a reason.`}</p>
    <p>{`I work in and with startups. That caps my salary at Not Very High. And even that makes my life cushy enough.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/f178be0430ebc9ef8fdcc73434f643e6/01a87/6ou5Jf9.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "26.905829596412556%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'107\\'%20viewBox=\\'0%200%20400%20107\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f178be0430ebc9ef8fdcc73434f643e6/ca0a1/6ou5Jf9.webp 223w", "/static/f178be0430ebc9ef8fdcc73434f643e6/75680/6ou5Jf9.webp 445w", "/static/f178be0430ebc9ef8fdcc73434f643e6/8d1ba/6ou5Jf9.webp 890w", "/static/f178be0430ebc9ef8fdcc73434f643e6/6f638/6ou5Jf9.webp 1288w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f178be0430ebc9ef8fdcc73434f643e6/e92b6/6ou5Jf9.png 223w", "/static/f178be0430ebc9ef8fdcc73434f643e6/e66bf/6ou5Jf9.png 445w", "/static/f178be0430ebc9ef8fdcc73434f643e6/4ef49/6ou5Jf9.png 890w", "/static/f178be0430ebc9ef8fdcc73434f643e6/01a87/6ou5Jf9.png 1288w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/f178be0430ebc9ef8fdcc73434f643e6/4ef49/6ou5Jf9.png",
                "alt": "Engineers back home don't make much",
                "title": "Engineers back home don't make much",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Engineers back home don't make much`}</figcaption>{`
  `}</figure></p>
    <p>{`Engineers back home don't make much`}</p>
    <p>{`Back home a well paid engineer with a local job makes around $25,000/year.`}</p>
    <p>{`In San Francisco I can save almost that much every year even without my side hustle. You need about $5000/month for a normal life with an SO. You won't take Uber all the time and maybe you can stick to just 1 latte per day, eat out only a few times per week, and keep it to 1 maybe 2 vacations per year.`}</p>
    <p>{`Wait ... that sounds pretty cushy!`}</p>
    <p>{`With a normal software engineer at a startup salary, you'll still have way more than $1000 left over every month. Maybe that doesn't sound like a lot to you. To me it was unimaginable just 5 years ago.`}</p>
    <p>{`Only people back home I know who pull that off are engineers playing the income arbitrage game. Live in a cheap country, work remotely for a rich country.`}</p>
    <p>{`But that makes you lazy.`}</p>
    <p>{`And I have friends here who work with big companies. They make upwards of $300k per year. More cash left over than they know what to do with.`}</p>
    <h2 {...{
      "id": "the-environment-pushes-you-"
    }}>{`The environment pushes you 🔥`}</h2>
    <p><a parentName="p" {...{
        "href": "https://84wz7ux5rc.execute-api.us-east-1.amazonaws.com/default/screenshot-as-a-service-dev-screenshot-function?url=https://www.instagram.com/p/BtHOU-RlZwh/"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "64.1255605381166%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'256\\'%20viewBox=\\'0%200%20400%20256\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%205c0%205%200%205%202%205%202-1%202%200%202%204l-1%205c-3-1-3-1-3%2047a348%20348%200%20001%2051l-1%2010c0%2012%200%2013%202%2013l2%201c-1%201%200%201%201%201l2-1c-2-2%201-1%202%201%202%202%202%202-3%202-9-1-7%201%202%202h13l1-1%201%201%205%201c3%200%204%200%203%201-2%201%202%201%2011%201%2011%200%2012%200%2010%202l-4%201h-2%2023l8%201%208%201h-8c-8%200-8%200-5%201l12%202c7%200%208%201%207%202s-3%202-7%202l-4%201c2%201-1%201-23%201H42c-1-1-13%200-13%201l13%201%2015%201c1%202%204%202%2014%202%209%200%2012%200%2011%201-2%202-9%202-50%202H2l19%201%2016%201H0l2%201a1012%201012%200%200141%204H0v31h11l11%201h1l9%201a1305%201305%200%200181%207%20349%20349%200%200140%204%20749%20749%200%200165%205l10%201h2l8%201h5l9%201%204%201v-22h-11a1430%201430%200%20016-2l5%201v-77l-3%201-6%202-6%201c-3%202-22%202-24%200l-2-1-1-2h9c-1-2-8-3-10%200-2%202-4%203-13%204a1691%201691%200%2000-52%203c-2-1-2-2-1-2l-7%201-3-1%201-1%201-2c0-2%2016-9%2025-11%206-2%2022-3%2022-2l-2%205c-2%205-3%207-1%207a484%20484%200%200151-6c-5%200-5-2%200-2%205-1%207%200%2013%202l7%202c2%200%202-9%202-62a474%20474%200%2000-1-64c4-2-1-3-38-3a309%20309%200%2000-41%201h-22c0-1-1-1-1%201l-2%201-1-2-1%203c1%202-1%203-2%201l-1-2v2c2%203%201%204-2%203l-1%201v1l-1-1-1-3c-1-1-2-2-1-3h1c0%202%201%202%202%200%203-1%201-3-3-3-2%200-3%200-2%202h-2c-1-2-3-2-8-2-6%200-6%200-6%202%201%202%201%202-1%202l-19-4%203%2090%201%2017%205-1h6v3c0%203%200%203-7%203l-16%201h-9v-3c0-3%200-3%203-3h2V90c-2-52-2-65-1-67l6-11c0-1-3-2-10-3h-6l-1-4V0h-6c-6%200-6%200-3%201%204%201%206%209%203%2010l-2%201%202%208%202%207%203%2010v17a213%20213%200%20003%2060H62v4l-1%203-2-2c0-2-1-3-2-3-2%200-2%200-1%203%200%202%200%202-5%202l-5-1h-1c0%202-2%201-2-2%200-2%200-2-1-1-1%202-2%200-3-4v-4h5l13-1h7l-1-16V76h9c1%200%202%200%202-2l-2-1c-1%201-1%200-2-1%200-2-1-2-2-2l-3%201-1%202-1%201-1-10a404%20404%200%2000-2-46V1h-6C43%201%2034%202%2034%203l-1-1c0-2-7-3-7-1h-1l-1-1-1%201c1%202%200%203-3%202l-3-2h-6l2%201h3v1c-1%202-6%203-7%202V4l2-1H8L6%204V1H1L0%205m274%201h-1c-2%200-2%200-1%201%202%200%202%200%200%201s-3%202-3%205l-1%202c-2%200-2%200-1%203%203%205%205%206%208%206%206%200%207-1%208-2h-1l-1-1c1-1%201-1-1-1-2%201-3%200-1-2s1-7-1-7c-2-1-3-4%200-4V6h-4M7%2011l1%203%203%202h2l2%202v1h-1l-1%202-1%202-1-2c1-1%200-2-2-2-3-1-4%200-3%204%200%202%200%202%201%200%200-2%202-3%202%200l1%201%202%202c1%203%201%203-1%203l-4-2c-1-1-1%200-1%209a3879%203879%200%20013%2076c-1-4-4-4-4%200%200%203%201%203%202%203%203%200%204%202%204%205%200%202%200%202%208%202%2013%200%2013%200%2013-9%200-3%200-4-3-4h-4v-6a661%20661%200%2001-2-44v-4h9l-1-5v-5l-7-1-7-1a3211%203211%200%2001-2-18c0-3%200-4%204-4l3%201%204%201c6%201%206-4-1-5l-10-4c0-3-3-5-4-3H7m131%2012l-1%202-1%201v1h-1c-1-1-1-1-1%201l-1%203v3c0%202%200%202%201%201%201-2%201-1%201%203-1%205-2%209-4%209v10a6547%206547%200%20005%2048c-3%200-3%200-3%203s0%203%205%203c10%200%2013-1%2012-4%200-2-1-2-5-2h-5l1-4%201-3%201-3-1-2h-2l2-1%201-12a909%20909%200%2001-3-58l-2%201m14%2025c2%2040%202%2043%205%2043h2V74l-2-32V26l-3-1h-2v23m30-11c-1%203-1%2015%201%2043v11l2-2c2-2%202-3%201-28%200-26-1-30-4-24m-14%2082h4c9%200%2022%202%2032%205%205%202%207%202%2015%201%2011%200%2012%200%205-2l-11-2c2%201-2%201-17-1-11-2-22-2-28-1m-109%209l-8%201c-8%200-8%200-7%203%200%201%201%202%203%202l3%201v1l2-1c0-2%202-3%203-1a615%20615%200%200127-3c0-2-1-2-11-2-7%200-11%200-10-1l-1-1-1%201m69%203l-7%201c-6%200-7%200-6%201%202%202-2%203-4%202-3-3-17-3-17%200%200%201%201%202%203%202l3%202c1%201%201%201%202-1l1-1%201%202-2%203c-6%203-5%204%201%201s11-4%2010-2c-1%201-1%201%201%201%203-2%2014-3%2013-2l2%201%202-2c1-1%200-1-1-1-5%201-12%200-10-1%204-2%208-2%208-1v1l5-3v-2c1-2%201-2-2-2l-3%201M0%20234v21h256l-1-10v-11a746%20746%200%2000-108-9l-13-1-9-1-10-1h-5l-1%201v-1l-1-1h-2l-6-1c-4%200-5%200-5%202h-8l3-1%203-2h-5c-5%200-5%200-4%202l-1%202-1-1h-1l-1-1c1-2-1-3-5-3a1957%201957%200%2001-71-6H0v22\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/245a61a9c06382a243f6c62b414d3f00/ca0a1/techletter.app-screenshot-1555345778530.webp 223w", "/static/245a61a9c06382a243f6c62b414d3f00/75680/techletter.app-screenshot-1555345778530.webp 445w", "/static/245a61a9c06382a243f6c62b414d3f00/0ba47/techletter.app-screenshot-1555345778530.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/245a61a9c06382a243f6c62b414d3f00/e92b6/techletter.app-screenshot-1555345778530.png 223w", "/static/245a61a9c06382a243f6c62b414d3f00/e66bf/techletter.app-screenshot-1555345778530.png 445w", "/static/245a61a9c06382a243f6c62b414d3f00/6af66/techletter.app-screenshot-1555345778530.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/245a61a9c06382a243f6c62b414d3f00/6af66/techletter.app-screenshot-1555345778530.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`That's my favorite part of San Francisco. The fire under butt.`}</p>
    <p>{`In San Francisco you never feel like you're making it. You never feel like you're crazy successful. You never feel like you're out of the woods.`}</p>
    <p>{`One bad month or two and you're digging into your long term savings. You have short-term cash buffers because d'oh.`}</p>
    <p>{`You don't want to dig into long term savings. Those exist for 70 year old you and for any Life Stuff that comes up.`}</p>
    <p>{`Everyone in San Francisco is always crushing it. Or says that they are.`}</p>
    <p>{`And that's a good thing.`}</p>
    <p>{`You are the average of the 5 people you spend most time with. You can't fight your environment. But you can change it.`}</p>
    <p>{`That said ...`}</p>
    <h2 {...{
      "id": "sf-is-not-an-entrepreneurial-mecca"
    }}>{`SF is not an entrepreneurial mecca`}</h2>
    <p>{`This one surprised me.`}</p>
    <p>{`I always thought San Francisco was an entrepreneurial mecca. But it isn't. San Francisco is a careerist mecca.`}</p>
    <p>{`People don't come here to start companies. People come here to get rich `}<em parentName="p">{`in`}</em>{` companies. It's too expensive to be on your own.`}</p>
    <p>{`Come here, make a bunch of money, leave. 5 to 10 years.`}</p>
    <p>{`Switch a bunch of jobs, play the career game, climb the startup ladder, and get the hell out. Catalyst for leaving is often kids or a good liquidity event.`}</p>
    <p>{`Some just get tired of the grind.`}</p>
    <p>{`Fire under butt is great when you want to grow, terrible when you need some rest.`}</p>
    <h2 {...{
      "id": "️"
    }}>{`✌️`}</h2>
    <p>{`And that's what I learned about San Francisco in my 4 year stint so far`}</p>
    <ol>
      <li parentName="ol">{`Your code doesn't matter`}</li>
      <li parentName="ol">{`Shipped beats perfect`}</li>
      <li parentName="ol">{`Everything's a prototype`}</li>
      <li parentName="ol">{`Play the career game`}</li>
      <li parentName="ol">{`It's expensive for a reason`}</li>
    </ol>
    <p>{`Happy Monday ~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      